import React, { useRef, useState } from 'react';
import * as IoC from 'typescript-ioc';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { EventStatus, IEventDetailsDTO, IEventsDAO } from '../../../../api/events.dao';
import Text from '../../../global/text/text';
import { stringifyStaffMembers } from '../../../../infrastructure/utils/users.utils';
import EventNameColumn from './_event-name-column/_event-name-column';
import { formatUTCForAnonymizationDate } from '../../../../infrastructure/utils/date.util';
import { LinkTargetValue, YES } from '../../../../constants/common.constants';
import { Routes } from '../../../../routes-table';
import { Link } from '../../../../infrastructure/components/router-extensions';
import { SalesforceOrderViewModel } from '../../_manage-event/shared/salesforce-order/salesforce-order.viewmodel';
import { SalesforceOrderModal } from '../../_manage-event/shared/salesforce-order/salesforce-order-modal.presentation';

interface IProps {
    event: IEventDetailsDTO;
    accountId: number;
    startEditEvent: () => void;
    isSelected: boolean;
    searchQuery?: string;
    navigateToEventRoute: (eventId: number) => void;
    isInternalEventLinksPermission?: boolean;
    anonymizationEnabled: boolean;
    eventBlueprintName?: string;
}

const EventListItem = (props: IProps) => {
    const {
        event,
        isSelected,
        startEditEvent,
        searchQuery,
        navigateToEventRoute,
        isInternalEventLinksPermission,
        anonymizationEnabled,
        accountId,
        eventBlueprintName
    } = props;
    const {
        name,
        startDateTime,
        endDateTime,
        eventId,
        timeZone,
        oeNumber,
        orderId,
        externalId,
        guidelinesLink,
        projectPlanLink,
        type,
        coordinators,
        deliveryTeamMembers,
        anonymized,
        anonymizationDate,
        eventStatus,
    } = event;

    const cellEffectClasses = classnames({
        'active': isSelected,
        'hoverable': !isSelected
    });

    const [salesforceOrderVM, setSalesforceOrderVM] = useState<SalesforceOrderViewModel | null>(null);
    const isFetchingEventDetails = useRef(false);

    const openSalesforceOrder = async (): Promise<void> => {
        if (salesforceOrderVM) {
            salesforceOrderVM.open();
            return;
        }
        if (isFetchingEventDetails.current) {
            return;
        }
        isFetchingEventDetails.current = true;
        let fullEvent = event;
        try {
            fullEvent = await IoC.Container.get(IEventsDAO).getEventDetails({ eventId: eventId, accountId: accountId });
        } catch (error) {
            // Fall back to the list-row event rather than blocking View Order entirely.
            console.error('Failed to fetch full event details for View Order', error);
        }
        isFetchingEventDetails.current = false;
        const vm = new SalesforceOrderViewModel(fullEvent, accountId);
        setSalesforceOrderVM(vm);
        vm.open();
    };

    let anonymizationDateString = '';

    if (anonymizationEnabled && anonymizationDate) {
        anonymizationDateString = formatUTCForAnonymizationDate(anonymizationDate);
    }

    const eventCancelledClass = classnames({ 'events-list-item__cancelled': eventStatus === EventStatus.CANCELLED });

    const routeParams = {
        accountId: accountId.toString(10),
        eventId: eventId.toString(10),
    };
    return (
        <>
        <tr className={classnames('events-list-item', eventCancelledClass)} onDoubleClick={() => navigateToEventRoute(eventId)}>
            <td className={cellEffectClasses}>
                <EventNameColumn
                    name={name}
                    startDateTime={startDateTime}
                    endDateTime={endDateTime}
                    timeZone={timeZone}
                    onEditEvent={startEditEvent}
                    searchQuery={searchQuery}
                    wrapperClassName='event-name-column__date-long'
                />
            </td>
            <td className={cellEffectClasses}></td>
            <Text className={cellEffectClasses} tag='td' message={type} withEllipsis/>
            <Text className={cellEffectClasses} tag='td' message={eventBlueprintName} withEllipsis/>
            <td className={classnames(cellEffectClasses)}>
                <Text message={oeNumber} highlightValue={searchQuery} withHighlight withEllipsis/>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <Text message={externalId} highlightValue={searchQuery} withHighlight withEllipsis/>
            </td>
            <td className={classnames(cellEffectClasses, 'column-with-filters')}>
                <div title={stringifyStaffMembers(coordinators).join('\n')}>
                    {stringifyStaffMembers(coordinators).map((person, index) => {
                        if (index <= 1) return <Text key={person} message={person} withEllipsis withoutTitle/>;
                    })}
                </div>
            </td>
            <td className={classnames(cellEffectClasses, 'column-with-filters')}>
                <div title={stringifyStaffMembers(deliveryTeamMembers).join('\n')}>
                    {stringifyStaffMembers(deliveryTeamMembers).map((person, index) => {
                        if (index <= 1) return <Text key={person} message={person} withEllipsis withoutTitle/>;
                    })}
                </div>
            </td>
            {
                anonymizationEnabled && <td className={classnames(cellEffectClasses)}>
                    <Text message={anonymized ? YES : ''} withEllipsis/>
                </td>
            }
            {
                anonymizationEnabled && <td className={classnames(cellEffectClasses)}>
                    <Text message={anonymizationDateString} withEllipsis/>
                </td>
            }
            <td className={classnames(cellEffectClasses)}>
                {isInternalEventLinksPermission && orderId &&
                <button
                    type='button'
                    className='materials-related-link events-list-item__view-order'
                    onClick={openSalesforceOrder}
                >
                    <Text id='event.viewOrder' message='View Order' description='Link label' withEllipsis/>
                </button>
                }
            </td>
            <td className={classnames(cellEffectClasses)}>
                {isInternalEventLinksPermission && guidelinesLink &&
                <a href={guidelinesLink} target={LinkTargetValue.BLANK} className='materials-related-link'>
                    <Text id='event.guidelines' message='Guidelines' description='Link label' withEllipsis/>
                </a>
                }
            </td>
            <td className={classnames(cellEffectClasses)}>
                {isInternalEventLinksPermission && projectPlanLink &&
                <a href={projectPlanLink} target={LinkTargetValue.BLANK} className='materials-related-link'>
                    <Text id='event.projectPlan' message='Project Plan' description='Link label' withEllipsis/>
                </a>
                }
            </td>
            <td className={classnames(cellEffectClasses, 'align-right')}>
                <Link routeEnum={Routes.Meetings} routeParams={routeParams} className='manage-button'>
                    <span className='default-button default-button--slim visible-while-hovered'>
                        <FormattedMessage
                            description='Button text'
                            defaultMessage='Manage'
                            id='oe.common.manage'
                        />
                    </span>
                </Link>

            </td>
        </tr>
        {salesforceOrderVM && <SalesforceOrderModal vm={salesforceOrderVM} />}
        </>
    );
};

export { EventListItem };
